import React, {Fragment, useEffect, useState} from "react";
import FolderIcon from "@mui/icons-material/Folder";
import GridOnIcon from "@mui/icons-material/GridOn";
import ListIcon from "@mui/icons-material/List";
import {Box} from "@mui/material";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {connect} from "react-redux";
import {useLocation} from "react-router-dom";
import {HashLink} from "react-router-hash-link";
import {getNodeCategoriesInternalUrl, getPackagesInternalUrl} from "../../constants/appRoutes";
import CatalogInfoButton from "../catalog-info-button";
import CatalogMetadataButton from "../catalog-metadata-button";
import CategoriesTree from "../categories-tree";
import CustomEmpty from "../custom-empty";
import CustomLink from "../custom-link";
import DetailLevelSelect from "../detail-level-select";
import Footer from "../footer";
import Header from "../header";
import PackageList from "../package-list";
import Page from "../page";
import PageTitle from "../page-title";
import {
  CATALOG_NAVIGATION_MODE_GRID,
  CATALOG_NAVIGATION_MODE_LIST,
  CATALOG_NAVIGATION_MODE_TREE
} from "../settings-select/nodes-settings/node-settings-form";
import ResultTree from "./ResultTree";
import {getPageTitle} from "../../utils/other";
import {getFilteredTreeWithPaths, getMappedTree} from "../../utils/tree";
import {scrollResultsToDatasetByParam} from "./utils";
import "./style.css";

const mapStateToProps = state => ({
  filteredCatalog: state.detailLevel.filteredCatalog,
  detailLevel: state.detailLevel.detailLevel
});

const Results = ({
  query,
  hub,
  catalog,
  node,
  nodeCode,
  isDefault,
  categoryPath,
  subCategories,
  filters,
  filtersParams,
  detailLevel,
  filteredCatalog,
  datasets,
  isAccessible
}) => {
  const {t} = useTranslation();
  const location = useLocation();

  const [catalogNavigationMode, setCatalogNavigationMode] = useState(
    hub.nodes.find(({code}) => code.toLowerCase() === nodeCode.toLowerCase()).catalogNavigationMode
  );

  useEffect(() => scrollResultsToDatasetByParam());

  const nodeMinimalInfo = hub.nodes.find(({code}) => code.toLowerCase() === nodeCode.toLowerCase());

  const nodeExtras = nodeMinimalInfo?.extras || [];
  const hideLabelOnCategoryWithImage = JSON.parse(
    nodeExtras.find(({key}) => key === "HideLabelOnCategoryWithImage")?.value || "[]"
  );

  if (filteredCatalog) {
    let categoryPathLabels = [];
    if (categoryPath) {
      if (categoryPath[0] === "uncategorized") {
        categoryPathLabels = [t("commons.catalog.uncategorized")];
      } else {
        let category;

        if (catalog.hasCategorySchemes) {
          category = catalog.categoryGroups.find(({id}) => id === categoryPath[0]);
        } else {
          category = catalog.categoryGroups[0].categories.find(({id}) => id === categoryPath[0]);
        }

        categoryPathLabels.push(category.label);
        for (let i = 1; i < categoryPath.length; i++) {
          category = (category.childrenCategories || category.categories).find(({id}) => id === categoryPath[i]);
          categoryPathLabels.push(category.label);
        }
      }
    }

    let categoryGroupsWithCount, uncategorizedDatasetsCount;

    if (query) {
      const testDSForQuery = ds =>
        (ds.title || "").toLowerCase().includes(query.toLowerCase()) ||
        !!ds.keywords?.find(keyword => keyword.toLowerCase().includes(query.toLowerCase()));

      const testCatForFilters = cat => filters.length === 0 || filters.includes(cat.id);

      const dssForCount = []; // satisfy only query (necessary to display count numbers)
      const dssForResults = []; // satisfy query and filters

      if (filteredCatalog.uncategorizedPackages && filteredCatalog.uncategorizedPackages.length > 0) {
        filteredCatalog.uncategorizedPackages.forEach(datasetId => {
          const ds = catalog.packageMap[datasetId];

          ds.categoryPath = ["uncategorized"];

          if (testDSForQuery(ds)) {
            dssForCount.push(ds);
            if (filters.length === 0 || filters.includes("uncategorized")) {
              dssForResults.push(ds);
            }
          }
        });
      }

      const searchRecursive = (categories, categoryPath, overrideCatSatisfyFilters = false) => {
        categories.forEach(c => {
          const catSatisfyFilters = overrideCatSatisfyFilters || testCatForFilters(c);

          c.packageIdentifiers.forEach(id => {
            const ds = {
              ...catalog.packageMap[id],
              identifier: id,
              categoryPath: [...categoryPath, c.id]
            };
            if (testDSForQuery(ds)) {
              dssForCount.push(ds);
              if (catSatisfyFilters) {
                dssForResults.push(ds);
              }
            }
          });

          searchRecursive(c.childrenCategories, [...categoryPath, c.id], catSatisfyFilters);
        });
      };

      if (filteredCatalog.hasCategorySchemes) {
        // with category schemes
        filteredCatalog.categoryGroups.forEach(group => searchRecursive(group.categories, [group.id]));
      } else if (filteredCatalog.categoryGroups.length === 1) {
        // no category schemes
        searchRecursive(filteredCatalog.categoryGroups[0].categories, []);
      }

      const dssSatisfyQueryMap = {};
      dssForCount.forEach(ds => (dssSatisfyQueryMap[ds.identifier] = ds));

      const recursiveCountAndFilterZero = (tree, visited) =>
        tree
          .map(node => {
            const children = recursiveCountAndFilterZero(node.childrenCategories, visited).filter(
              ({count}) => count > 0
            );

            const uniqueDssSatisfyQueryCount =
              node.packageIdentifiers?.filter(dsId => {
                if (dssSatisfyQueryMap[dsId] !== undefined) {
                  if (visited[dsId]) {
                    return false;
                  } else {
                    visited[dsId] = true;
                    return true;
                  }
                } else {
                  return false;
                }
              }).length || 0;

            return {
              ...node,
              childrenCategories: node.childrenCategories ? children : undefined,
              count: children.reduce((acc, node) => acc + node.count, 0) + uniqueDssSatisfyQueryCount
            };
          })
          .filter(({count}) => count > 0);

      categoryGroupsWithCount = filteredCatalog.categoryGroups
        .map(categoryGroup => {
          const categories = (categoryGroup?.categories || [])
            .map(category => {
              const visited = {};
              const childrenCategories = recursiveCountAndFilterZero(category.childrenCategories, visited);
              return {
                ...category,
                childrenCategories: childrenCategories,
                count:
                  childrenCategories.reduce((acc, node) => acc + node.count, 0) +
                  (category.packageIdentifiers?.filter(dsId => dssSatisfyQueryMap[dsId] !== undefined).length || 0)
              };
            })
            .filter(({count}) => count > 0);
          return {
            ...categoryGroup,
            categories: categories,
            count: categories.reduce((acc, node) => acc + node.count, 0)
          };
        })
        .filter(({count}) => count > 0);

      uncategorizedDatasetsCount = filteredCatalog.uncategorizedPackages.filter(
        ({identifier}) => dssSatisfyQueryMap[identifier] !== undefined
      ).length;

      // remove duplicates from results ( O(2n) )
      const resultsMap = {};
      dssForResults.forEach(ds => (resultsMap[ds.identifier] = ds));

      datasets = Object.keys(resultsMap).map(id => resultsMap[id]);
    }

    const filteredDatasets = detailLevel
      ? datasets.filter(({detailLevels: dls}) => detailLevel == null || (dls || []).includes(detailLevel))
      : datasets;

    const filteredSubCategories =
      detailLevel !== null && subCategories
        ? getFilteredTreeWithPaths(
            getMappedTree(subCategories, "childrenCategories", cat => ({
              ...cat,
              packageIdentifiers: (cat.packageIdentifiers || []).filter(id =>
                (catalog.packageMap[id].detailLevels || []).includes(detailLevel)
              )
            })),
            "childrenCategories",
            ({packageIdentifiers}) => packageIdentifiers && packageIdentifiers.length > 0
          )
        : subCategories;

    const categoriesWithImages = (filteredSubCategories || []).find(({image}) => !!image) !== undefined;

    const params = new URLSearchParams(location.search);
    params.set("accessible", "true");
    const paramsStr = params.toString();
    const path = location.pathname;

    const isTreeViewMode =
      catalogNavigationMode === CATALOG_NAVIGATION_MODE_TREE &&
      ((catalog.hasCategorySchemes && (categoryPath || []).length === 2) ||
        (!catalog.hasCategorySchemes && (categoryPath || []).length === 1));

    let catalogForTreeView = null;

    if (isTreeViewMode) {
      const categorySchemaId = catalog.hasCategorySchemes ? categoryPath[0] : filteredCatalog.categoryGroups[0].id;
      const categoryId = catalog.hasCategorySchemes ? categoryPath[1] : categoryPath[0];

      const categoryGroup = filteredCatalog.categoryGroups.find(({id}) => id === categorySchemaId);

      catalogForTreeView = {
        ...filteredCatalog,
        hasCategorySchemes: false,
        categoryGroups: categoryGroup
          ? [
              {
                ...categoryGroup,
                categories: categoryGroup.categories.find(({id}) => id === categoryId).childrenCategories
              }
            ]
          : [],
        uncategorizedPackages: null,
        rootPackages: filteredDatasets
      };
    }

    return (
      <Fragment>
        {!isAccessible && (
          <a href={path + (paramsStr.length > 0 ? "?" : "") + paramsStr} target="_self" className="skip-link sr-only">
            {t("commons.hashLinks.accessible")}
          </a>
        )}
        <HashLink to={{hash: "#main", search: location.search}} className="skip-link sr-only">
          {t("commons.hashLinks.main")}
        </HashLink>
        <HashLink to={{hash: "#footer", search: location.search}} className="skip-link sr-only">
          {t("commons.hashLinks.footer")}
        </HashLink>
        <Page
          title={
            query
              ? getPageTitle(
                  [t("scenes.results.title", {query: query, nodeName: nodeMinimalInfo?.name}), hub?.hub?.name],
                  t
                )
              : getPageTitle(
                  [categoryPathLabels[categoryPathLabels.length - 1], nodeMinimalInfo?.name, hub?.hub?.name],
                  t
                )
          }
        >
          <Header isDefault={isDefault} selectedCategoryPath={categoryPath} />
          <Box
            component="main"
            sx={{
              width: "100%",
              minHeight: "100%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              flexDirection: "column"
            }}
            id="main"
            className={`results`}
          >
            <Container>
              <Grid container justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
                <Grid>
                  {query ? (
                    <PageTitle
                      title={t("scenes.results.searchTitle", {query})}
                      breadcrumbItems={[
                        {
                          to: getNodeCategoriesInternalUrl(node.code),
                          label: t("scenes.categories.title")
                        }
                      ]}
                    />
                  ) : (
                    <PageTitle
                      title={categoryPathLabels[categoryPath.length - 1]}
                      breadcrumbItems={[
                        {
                          to: getNodeCategoriesInternalUrl(node.code),
                          label: t("scenes.categories.title")
                        },
                        ...(categoryPath || []).map((id, idx) => ({
                          to:
                            idx < categoryPath.length - 1 && id !== "uncategorized"
                              ? getPackagesInternalUrl(node.code, categoryPath.slice(0, idx + 1))
                              : null,
                          label: categoryPathLabels[idx]
                        }))
                      ]}
                    />
                  )}
                </Grid>
                {!isAccessible && catalogNavigationMode !== CATALOG_NAVIGATION_MODE_TREE && (
                  <Grid sx={{marginTop: theme => theme.spacing(3)}}>
                    {filteredDatasets.length > 0 && (
                      <Tooltip
                        title={
                          catalogNavigationMode === CATALOG_NAVIGATION_MODE_LIST
                            ? t("components.results.actions.listModeToggle.grid.tooltip")
                            : t("components.results.actions.listModeToggle.list.tooltip")
                        }
                      >
                        <IconButton
                          id="results__list-mode-toggle"
                          onClick={() =>
                            setCatalogNavigationMode(
                              catalogNavigationMode === CATALOG_NAVIGATION_MODE_LIST
                                ? CATALOG_NAVIGATION_MODE_GRID
                                : CATALOG_NAVIGATION_MODE_LIST
                            )
                          }
                          aria-label={
                            catalogNavigationMode === CATALOG_NAVIGATION_MODE_LIST
                              ? t("components.results.actions.listModeToggle.grid.ariaLabel")
                              : t("components.results.actions.listModeToggle.list.ariaLabel")
                          }
                        >
                          {catalogNavigationMode === CATALOG_NAVIGATION_MODE_LIST ? <GridOnIcon /> : <ListIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                )}
              </Grid>
              {catalog.detailLevels.length > 0 && (
                <Grid container justifyContent="flex-start" alignItems="center" spacing={2} sx={{padding: "8px 4px"}}>
                  <Grid>
                    <Typography>{t("components.results.detailLevelSelectTitle")}</Typography>
                  </Grid>
                  <Grid>
                    <DetailLevelSelect />
                  </Grid>
                </Grid>
              )}
              <Grid container justifyContent="space-between" sx={{marginTop: "8px"}}>
                {query && (
                  <Grid id="results__tree-grid" style={{width: 256}}>
                    <ResultTree
                      query={query}
                      filters={filtersParams}
                      catalog={filteredCatalog}
                      node={node}
                      datasets={filteredDatasets}
                      categoryGroupsWithCount={categoryGroupsWithCount}
                      uncategorizedDatasetsCount={uncategorizedDatasetsCount}
                    />
                  </Grid>
                )}
                <Grid style={{width: query ? `calc(100% - 256px - 16px)` : "100%"}}>
                  {!query ? (
                    <Fragment>
                      <nav aria-label={t("components.results.subcategories.ariaLabel")}>
                        {isTreeViewMode ? (
                          <Box
                            sx={{
                              marginTop: "-48px",
                              zIndex: 1
                            }}
                          >
                            <CategoriesTree
                              catalog={catalogForTreeView}
                              node={node}
                              initialPath={categoryPath}
                              showPackageList
                              showExpandControls
                              showCategoriesFirst
                            />
                          </Box>
                        ) : (
                          <Grid container spacing={2} sx={{marginBottom: theme => theme.spacing(1)}}>
                            {(filteredSubCategories || []).map(
                              ({id, label, packageIdentifiers, description, metadataUrl, image}) => (
                                <Grid
                                  key={id}
                                  size={{
                                    xs: categoriesWithImages ? 4 : 12,
                                    sm: categoriesWithImages ? 4 : 12,
                                    md: categoriesWithImages ? 3 : 6,
                                    lg: categoriesWithImages ? 2 : 4,
                                    xl: categoriesWithImages ? 2 : 4
                                  }}
                                >
                                  <Card
                                    sx={{
                                      height: "100%",
                                      display: "flex",
                                      flexDirection: "row",
                                      justifyContent: "space-between"
                                    }}
                                  >
                                    <CustomLink
                                      to={getPackagesInternalUrl(node.code.toLowerCase(), [...categoryPath, id])}
                                      text={
                                        hideLabelOnCategoryWithImage && image
                                          ? null
                                          : `${label} ${
                                              packageIdentifiers && packageIdentifiers.length
                                                ? `(${packageIdentifiers.length})`
                                                : ""
                                            }`
                                      }
                                      textStyle={
                                        hideLabelOnCategoryWithImage && image
                                          ? {
                                              padding: 0
                                            }
                                          : {
                                              padding: 16,
                                              fontSize: 20
                                            }
                                      }
                                      image={image}
                                      imageStyle={{
                                        maxHeight: undefined
                                      }}
                                      icon={image ? undefined : <FolderIcon />}
                                    />
                                    {(description || metadataUrl) && (
                                      <CardActions sx={{padding: "4px 8px"}}>
                                        {description && <CatalogInfoButton description={description} />}
                                        {metadataUrl && (
                                          <CatalogMetadataButton metadataUrl={metadataUrl} iconSize="small" />
                                        )}
                                      </CardActions>
                                    )}
                                  </Card>
                                </Grid>
                              )
                            )}
                          </Grid>
                        )}
                      </nav>
                      {filteredDatasets.length > 0 && !isTreeViewMode && (
                        <Divider
                          sx={{
                            margin: "16px 0"
                          }}
                        />
                      )}
                    </Fragment>
                  ) : (
                    filteredDatasets &&
                    filteredDatasets.length > 0 && (
                      <Typography
                        variant="body1"
                        sx={{
                          marginBottom: theme => theme.spacing(1),
                          color: theme => theme.palette.text.primary
                        }}
                      >
                        {filteredDatasets.length > 1
                          ? t("scenes.results.datasetsCount.plural", {datasetsCount: filteredDatasets.length})
                          : t("scenes.results.datasetsCount.singular", {datasetsCount: filteredDatasets.length})}
                        :
                      </Typography>
                    )
                  )}
                  {isTreeViewMode ? null : filteredDatasets.length > 0 ? (
                    <nav aria-label={t("components.results.datasets.ariaLabel")}>
                      <Grid id="results__datasets-grid" container spacing={2}>
                        <PackageList
                          node={node}
                          catalog={catalog}
                          packages={filteredDatasets}
                          query={query}
                          catalogNavigationMode={catalogNavigationMode}
                        />
                      </Grid>
                    </nav>
                  ) : query || !filteredSubCategories || !filteredSubCategories.length ? (
                    <CustomEmpty
                      text={t("scenes.results.datasetsCount.none")}
                      textStyle={{fontSize: 16}}
                      style={{marginTop: 32, marginLeft: query ? -128 : 0}}
                    />
                  ) : null}
                </Grid>
              </Grid>
            </Container>
            <Footer />
          </Box>
        </Page>
      </Fragment>
    );
  } else {
    return null;
  }
};

export default connect(mapStateToProps)(Results);
